
import { Injectable } from '@angular/core';
import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import * as moment from 'moment-timezone';
import * as momentTimeZone from 'moment-timezone';
import { ApiCallStatusService } from './services/api-call-status.service';

@Injectable({
  providedIn: 'root',
})
export class AxiosInterceptorService {
  private axiosInstance: AxiosInstance;

  constructor(private apiCallStatusSrvs: ApiCallStatusService) {
    this.axiosInstance = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    // Create Axios instance with custom settings if needed
    return axios.create();
  }

  private setupInterceptors(): void {
    const requestInterceptor = this.requestInterceptor.bind(this);
    const responseInterceptor = this.responseInterceptor.bind(this);
    const errorInterceptor = this.errorInterceptor.bind(this);

    this.axiosInstance.interceptors.request.use(
      requestInterceptor,
      errorInterceptor
    );

    this.axiosInstance.interceptors.response.use(
      responseInterceptor,
      errorInterceptor
    );
  }

  private requestInterceptor(config: AxiosRequestConfig): AxiosRequestConfig {
    this.apiCallStatusSrvs.incrementRequestCount();

    const isPublicRequest =
      (config.url || '').includes('Account/Login') ||
      (config.url || '').includes('Account/RefreshToke') ||
      (config.url || '').includes('AppVersion/GetAppLatestVersion');

    if (isPublicRequest) {
      return config;
    }

    const userTimeZone = moment.tz.guess();

    try {
      const selectedCompanyBranch = JSON.parse(localStorage.getItem("selectedCompanyBranch") || 'null');
      const selectedUserObj = JSON.parse(localStorage.getItem("user") || 'null');
      const token = localStorage.getItem("token");
      const branchTimeZoneName = typeof selectedCompanyBranch?.timeZoneName === 'string'
        ? selectedCompanyBranch.timeZoneName.trim()
        : '';
      const timeZoneId = Number(selectedCompanyBranch?.timeZoneId);

      if (
        !token ||
        !selectedUserObj?.userId ||
        !selectedCompanyBranch?.companyId ||
        !selectedCompanyBranch?.companyBranchId ||
        !branchTimeZoneName ||
        !Number.isFinite(timeZoneId) ||
        timeZoneId <= 0
      ) {
        this.apiCallStatusSrvs.decrementRequestCount();
        if (!window.location.pathname.includes('/authentication/login')) {
          window.location.href = '/authentication/login';
        }
        return Promise.reject(new Error('Session data is missing. Please log in again.')) as never;
      }

      config.headers['CompanyId'] = selectedCompanyBranch.companyId;
      config.headers['CompanyBranchId'] = selectedCompanyBranch.companyBranchId;
      config.headers['UserId'] = selectedUserObj.userId;
      config.headers['UserDateTime'] = new Date();
      config.headers['UserTimeZone'] = userTimeZone;
      config.headers['timeZoneName'] = branchTimeZoneName;
      config.headers['timeZoneId'] = timeZoneId;
    } catch (error) {
      console.error('Error parsing local storage items', error);
      this.apiCallStatusSrvs.decrementRequestCount();
      return Promise.reject(error) as never;
    }

    return config;
  }

  private responseInterceptor(response: AxiosResponse): any {
    this.apiCallStatusSrvs.decrementRequestCount();
    return response;
  }

  private errorInterceptor(error: AxiosError): Promise<any> {
    console.error('Axios request error: ', error);
    this.apiCallStatusSrvs.decrementRequestCount();
    return Promise.reject(error);
  }

  public get(endpoint: string): Promise<any> {
    return this.axiosInstance.get(endpoint);
  }

  public post(endpoint: string, data: any, customHeader?: any): Promise<any> {
    return this.axiosInstance.post(endpoint, data, customHeader);
  }

  public put(endpoint: string, data: any): Promise<any> {
    return this.axiosInstance.put(endpoint, data);
  }

  public patch(endpoint: string, data: any): Promise<any> {
    return this.axiosInstance.patch(endpoint, data);
  }

  public delete(endpoint: string): Promise<any> {
    return this.axiosInstance.delete(endpoint);
  }
}
